import React, { useMemo } from "react";
import { useProject } from "../state/projectStore";
import { slotsForPage } from "../lib/imposition";
import Frame, { CutMarksOverlay } from "./Frame";
import { RulerH, RulerV, RULER_SIZE } from "./Ruler";

export default function PageView({ pageIndex, pxPerMm }) {
  const { state, page, layout, selectSlot } = useProject();
  const pageOffset = (state.pageOffsets || {})[pageIndex] || { x: 0, y: 0 };

  const { slots } = useMemo(
    () =>
      slotsForPage({
        page,
        piece: state.piece,
        copies: state.copies,
        separation: state.separation,
        bleed: state.bleed,
        pageIndex,
      }),
    [page, state.piece, state.copies, state.separation, state.bleed, pageIndex]
  );

  const paperWpx = page.width_mm * pxPerMm;
  const paperHpx = page.height_mm * pxPerMm;

  // Printable area inside paper (centered)
  const printableXmm = (page.width_mm - page.printable_w_mm) / 2;
  const printableYmm = (page.height_mm - page.printable_h_mm) / 2;

  const shouldShowMarks = (() => {
    const mode = state.cutMarks.pages || "all";
    if (mode === "all") return true;
    if (mode === "first") return pageIndex === 0;
    if (mode === "odd") return (pageIndex + 1) % 2 === 1;
    if (mode === "even") return (pageIndex + 1) % 2 === 0;
    return true;
  })();

  return (
    <div className="inline-flex flex-col items-start" data-testid={`page-view-${pageIndex}`}>
      {/* Page label */}
      <div className="flex items-center justify-between w-full mb-1 px-1">
        <span className="text-[10px] font-mono text-[#9CA3AF]">
          Página {pageIndex + 1} / {layout.pages || 1}
        </span>
        <span className="text-[10px] font-mono text-[#6B7280]">
          {page.width_mm} × {page.height_mm} mm
        </span>
      </div>

      {/* Ruler row + paper */}
      <div className="flex" style={{ paddingLeft: RULER_SIZE }}>
        <div style={{ width: `${paperWpx}px` }}>
          <RulerH widthMm={page.width_mm} pxPerMm={pxPerMm} />
        </div>
      </div>
      <div className="flex">
        <RulerV heightMm={page.height_mm} pxPerMm={pxPerMm} />
        <div
          className="relative bg-white shadow-[0_0_40px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
          style={{ width: `${paperWpx}px`, height: `${paperHpx}px` }}
          onClick={() => selectSlot(null)}
          data-testid={`paper-sheet-${pageIndex}`}
        >
          {/* Printable area indicator */}
          <div
            style={{
              position: "absolute",
              left: `${printableXmm * pxPerMm}px`,
              top: `${printableYmm * pxPerMm}px`,
              width: `${page.printable_w_mm * pxPerMm}px`,
              height: `${page.printable_h_mm * pxPerMm}px`,
              border: "1px dashed rgba(0, 229, 255, 0.3)",
              pointerEvents: "none",
              boxSizing: "border-box",
            }}
          />

          {/* Pieces */}
          {slots.map((slot) => (
            <Frame key={slot.index} slot={slot} pxPerMm={pxPerMm} pageOffset={pageOffset} />
          ))}

          {/* Cut marks overlay */}
          {shouldShowMarks && (
            <CutMarksOverlay
              slots={slots}
              cutMarks={state.cutMarks}
              pxPerMm={pxPerMm}
              pageWmm={page.width_mm}
              pageHmm={page.height_mm}
              pageOffset={pageOffset}
            />
          )}
        </div>
      </div>
    </div>
  );
}
